import { castDraft } from 'immer';
import { io } from 'socket.io-client';

import { aws_exports } from '@akin-home/amplify';
import { useHabitatStore } from '@akin-home/habitats/react';
import { Auth } from 'aws-amplify';
import { BleManager } from 'react-native-ble-plx';
import { useStateStore } from '../../stores/useStateStore';
import { showErrorToast } from '../../toasts';
import { HandshakePacket } from '../websockets/commands';
import { RobotSocket } from '../websockets/types';
import { RobotConnEventType, RobotStateSlice, RobotStore } from './types';
import { prefetchRobotDetails } from '../info/functions';
import { isWebBuild } from '@akin-home/utils/native';

/**
 * Store for all robot connection related items.
 */
export const createRobotStore: RobotStateSlice<RobotStore> = (set, get) => ({
  robotDevices: {},

  deleteRobotDevice: async (robotId) => {
    const robotDevice = get().robotDevices[robotId];
    if (
      robotDevice.websocketConnection?.connectionStatus === 'connected' ||
      robotDevice.websocketConnection?.connectionStatus === 'connecting'
    ) {
      robotDevice.websocketConnection.socket?.disconnect();
    }

    if (
      robotDevice.bluetoothConnection?.connectionStatus === 'connected' ||
      robotDevice.bluetoothConnection?.connectionStatus === 'connecting'
    ) {
      await robotDevice.bluetoothConnection.bluetoothDevice?.cancelConnection();
    }

    set((state) => {
      delete state.robotDevices[robotId];
    });
  },

  establishRobotWebsocketConnection: (robotDevice, robotUrl, params) => {
    const robotId = robotDevice.id;

    // Add default values for params if required
    params = {
      forceReconnection: false,
      ...params,
    };

    let isSameUriAsBefore = true;
    if (robotId) {
      isSameUriAsBefore =
        get().robotDevices[robotId]?.websocketConnection?.ipAddress ===
        robotUrl;
    }

    // Only create one instance
    const existingSocket =
      get().robotDevices[robotId]?.websocketConnection?.socket;

    if (existingSocket) {
      if (
        existingSocket.connected &&
        isSameUriAsBefore &&
        !params.forceReconnection
      ) {
        // We want to keep the existing socket, so just leave the function.
        return;
      } else {
        // Disconnect the socket so it can be replaced
        existingSocket.disconnect();
      }
    }

    set((state) => {
      if (state.robotDevices[robotId] === undefined) {
        state.robotDevices[robotId] = castDraft(robotDevice);
      }

      state.robotDevices[robotId].websocketConnection = castDraft({
        ...state.robotDevices[robotId].websocketConnection,
        ...robotDevice.websocketConnection,
        connectionStatus: 'connecting',
        ipAddress: robotUrl,
      });
    });

    const socket: RobotSocket = io(`ws://${robotUrl}/`, {
      transports: ['websocket', 'polling', 'webtransport'],
      path: '/websockets/socket.io/',
      extraHeaders: {
        device_name: 'pixi',
      },
      reconnectionAttempts: 10,
    });

    socket.onAny((args) => {
      console.log('Some message recvd', args);
    });

    socket.on('connect', async () => {
      console.log('New Socket Connection or Reconnection');

      const {
        aws_cloud_logic_custom: endpoints,
        aws_appsync_graphqlEndpoint,
        aws_appsync_region,

        aws_cognito_identity_pool_id,
        aws_cognito_region,
        aws_user_pools_id,
        aws_user_pools_web_client_id,
      } = aws_exports;

      endpoints.push({
        name: 'graphql',
        endpoint: aws_appsync_graphqlEndpoint,
        region: aws_appsync_region,
      });

      const handshakePacket: HandshakePacket = {
        user_id: useStateStore.getState().current_user_id,
        user_name: useStateStore.getState().current_user?.username,
        habitat_id: useHabitatStore.getState().habitat?.habitatID,
        refresh_token: (await Auth.currentSession())
          .getRefreshToken()
          .getToken(),

        endpoints,

        identity_pool_id: aws_cognito_identity_pool_id,
        region: aws_cognito_region,
        user_pools_id: aws_user_pools_id,
        user_pools_web_client_id: aws_user_pools_web_client_id,
      };

      const success: boolean = await socket
        .timeout(10000)
        .emitWithAck('handshake', handshakePacket)
        .catch(() => false);

      if (success) {
        set((state) => {
          state.robotDevices[robotId].websocketConnection = {
            ...state.robotDevices[robotId].websocketConnection,
            connectionStatus: 'connected',
          };
        });
      } else {
        console.error('Websocket handshake connection failed');
        showErrorToast({
          text1: 'Robot Connection Failed',
          text2:
            'Initiating connection with the robot failed. Please try again.',
        });
        socket.disconnect();
      }
    });

    socket.on('connect_error', (error) => {
      console.error(
        `Connection failed due to: ${error.name} ${
          socket.active ? '(will attempt to reconnect)' : ''
        }\n - ${error.message}`
      );

      set((state) => {
        state.robotDevices[robotId].websocketConnection = {
          ...state.robotDevices[robotId].websocketConnection,
          connectionStatus: 'disconnected',
        };
      });
    });

    socket.on('disconnect', (reason, details) => {
      console.warn(
        `Socket Disconnected due to: ${reason} ${
          socket.active ? '(will attempt to reconnect)' : ''
        }\n - ${details}`
      );

      set((state) => {
        state.robotDevices[robotId].websocketConnection = {
          ...state.robotDevices[robotId].websocketConnection,
          connectionStatus: 'disconnected',
        };
      });
    });

    set((state) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      state.robotDevices[robotId].websocketConnection!.socket = castDraft(
        socket as RobotSocket
      );
    });

    // Trigger the New Socket creation event listeners
    const robotConnection = get().robotDevices[robotId];
    get().robotConnEventListeners[RobotConnEventType.NewSocket]?.forEach((fn) =>
      fn(robotConnection)
    );

    prefetchRobotDetails(get().robotDevices[robotId]);
  },

  robotConnEventListeners: {
    [RobotConnEventType.NewSocket]: [],
  },

  addRobotConnEventListener: ({ event, callback }) => {
    set((state) => {
      state.robotConnEventListeners[event]?.push(callback);
    });

    return () => {
      set((state) => {
        state.robotConnEventListeners[event] = state.robotConnEventListeners[
          event
        ]?.filter((fn) => fn !== callback);
      });
    };
  },

  isConnected: (robotId) => {
    const robotDevice = get().robotDevices[robotId];

    if (robotDevice?.websocketConnection?.connectionStatus === 'connected') {
      return 'connected';
    } else if (
      robotDevice?.bluetoothConnection?.connectionStatus === 'connected' ||
      robotDevice?.bluetoothConnection?.connectionStatus === 'connecting' ||
      robotDevice?.websocketConnection?.connectionStatus === 'connecting'
    ) {
      return 'connecting';
    } else if (
      robotDevice?.bluetoothConnection?.connectionStatus === 'disconnecting'
    ) {
      return 'disconnecting';
    } else {
      return 'disconnected';
    }
  },

  BleManager: isWebBuild() ? null : new BleManager(),

  establishRobotBluetoothConnection: async (robotDevice, bluetoothDevice) => {
    const robotId = robotDevice.id;

    set((state) => {
      if (state.robotDevices[robotId] === undefined) {
        state.robotDevices[robotId] = castDraft(robotDevice);
      }

      state.robotDevices[robotId].bluetoothConnection = castDraft({
        ...state.robotDevices[robotId]?.bluetoothConnection,
        ...robotDevice.bluetoothConnection,
        connectionStatus: 'connecting',
        bluetoothDevice: bluetoothDevice,
        macAddress: bluetoothDevice.id,
      });
    });

    set((state) => {
      if (state.robotDevices[robotId].bluetoothConnection?.onDisconnectSub) {
        state.robotDevices[
          robotId
        ].bluetoothConnection?.onDisconnectSub?.remove();
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      state.robotDevices[robotId].bluetoothConnection!.onDisconnectSub =
        bluetoothDevice.onDisconnected(() => {
          set((state) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            state.robotDevices[robotId].bluetoothConnection!.connectionStatus =
              'disconnected';
          });
        });
    });

    try {
      await bluetoothDevice.connect({ timeout: 1000000 });
      await bluetoothDevice.discoverAllServicesAndCharacteristics();
    } catch (err) {
      console.log('Error in connecting to device', err);
    }

    const isBTConnected = (await bluetoothDevice?.isConnected()) ?? false;
    set((state) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      state.robotDevices[robotId].bluetoothConnection!.connectionStatus =
        isBTConnected ? 'connected' : 'disconnected';
    });

    return isBTConnected;
  },
});
